import * as THREE from 'three'
import { mulberry32 } from '../core/rng'
import { TREE_ATLAS } from './treeAtlas'
import type { AtlasCell } from './treeAtlas'

/**
 * Conifers built from photographs.
 *
 * Two generations of procedural tree stood here — stacked cones, then whorled
 * flat-shaded branches — and both hit the same wall: no amount of vertex
 * geometry makes needles. Every AAA snow game solves foliage the same way, and
 * this now does too: **alpha-tested cards** cut from photographs (generated
 * with Higgsfield, matted by its background remover, packed by
 * tools/make-treeatlas.mjs), hung on simple geometry.
 *
 * Two builds per archetype:
 *
 *   - **Cards** (near): the old trunk mesh — which was always fine — with a
 *     photographed branch spray on a quad where each procedural branch used to
 *     be. The whorl logic, droop, wind-flagging and gap distribution all
 *     survive unchanged; only what a branch *is* changed. Four vertices where
 *     eight triangles were, so the near tree got cheaper while gaining actual
 *     needles.
 *
 *   - **Star** (far, and near for the spar): three vertical planes through the
 *     trunk axis at 60° spacings, wearing a whole-tree photograph. Six
 *     triangles. The spar uses its photo at every distance because its whole
 *     character is the silhouette of one particular dead tree; assembling it
 *     from branch sprays would throw away exactly what the photograph knows.
 *
 * The trunk samples the atlas's solid-white cell, so one material (and one
 * draw call per bucket) serves bark and foliage: the trunk's vertex colours
 * carry the bark, the cards' colours stay near-white and the photo carries
 * everything else.
 */

type Vec = readonly [number, number, number]
type Col = readonly [number, number, number]

const BARK: Col = [0.19, 0.145, 0.115]
const WHITE_UV = TREE_ATLAS.white

class Soup {
  private readonly pos: number[] = []
  private readonly col: number[] = []
  private readonly uv: number[] = []

  tri(
    a: Vec, b: Vec, c: Vec,
    ca: Col, cb: Col, cc: Col,
    ua: readonly [number, number], ub: readonly [number, number], uc: readonly [number, number],
  ): void {
    this.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2])
    this.col.push(ca[0], ca[1], ca[2], cb[0], cb[1], cb[2], cc[0], cc[1], cc[2])
    this.uv.push(ua[0], ua[1], ub[0], ub[1], uc[0], uc[1])
  }

  /** A solid-colour triangle: UVs parked on the atlas's white cell. */
  flat(a: Vec, b: Vec, c: Vec, col: Col): void {
    const w: readonly [number, number] = [WHITE_UV.u0, WHITE_UV.v0]
    this.tri(a, b, c, col, col, col, w, w, w)
  }

  /** A textured quad: p00 bottom-left through p11 top-right of the cell. */
  quad(
    p00: Vec, p10: Vec, p11: Vec, p01: Vec,
    cell: AtlasCell, tint00: Col, tint10: Col, tint11: Col, tint01: Col,
    flipU: boolean,
  ): void {
    const u0 = flipU ? cell.u1 : cell.u0
    const u1 = flipU ? cell.u0 : cell.u1
    this.tri(p00, p10, p11, tint00, tint10, tint11,
      [u0, cell.v0], [u1, cell.v0], [u1, cell.v1])
    this.tri(p00, p11, p01, tint00, tint11, tint01,
      [u0, cell.v0], [u1, cell.v1], [u0, cell.v1])
  }

  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.pos), 3))
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.col), 3))
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(this.uv), 2))
    g.computeVertexNormals()
    g.computeBoundingSphere()
    return g
  }
}

export interface CardTreeOptions {
  /** Total height of the unit tree, metres. Instances scale from this. */
  height: number
  /** 'cards': trunk + branch cards. 'star': crossed whole-tree planes. */
  style: 'cards' | 'star'
  /** Whole-tree atlas cell, used by the star build. */
  treeCell: AtlasCell
  /** Branch-spray atlas cell, used by the cards build. */
  branchCell: AtlasCell
  whorls: number
  branchesPerWhorl: number
  /** Longest branch, as a fraction of height. */
  spread: number
  /** How hard the lowest branches hang, radians. */
  droop: number
  /** Fraction of branches simply missing. */
  gaps: number
  /** Windward stripping, 0..1 — see the flagged-tree note in obstacleMesh. */
  windFlag: number
  seed: number
}

export function buildCardTree(opts: CardTreeOptions): THREE.BufferGeometry {
  const s = new Soup()
  const rng = mulberry32(opts.seed)
  if (opts.style === 'star') {
    star(s, opts, rng)
  } else {
    trunk(s, opts.height)
    cards(s, opts, rng)
    spire(s, opts.height, rng)
  }
  return s.build()
}

/**
 * Three vertical planes at 60° spacings wearing the whole-tree photo.
 *
 * Slightly different tints per plane, so the intersection reads as depth
 * variation rather than a repeated image; slight height jitter per plane so
 * the top is not a perfect X from every angle.
 */
function star(s: Soup, opts: CardTreeOptions, rng: () => number): void {
  const H = opts.height
  const W = H * opts.treeCell.aspect
  for (let p = 0; p < 3; p++) {
    const a = (p / 3) * Math.PI + (rng() - 0.5) * 0.12
    const dx = Math.cos(a) * W * 0.5
    const dz = Math.sin(a) * W * 0.5
    const h = H * (0.96 + rng() * 0.08)
    const sink = H * 0.06
    const t = 0.9 + rng() * 0.1
    const tint: Col = [t, t, t]
    s.quad(
      [-dx, -sink, -dz], [dx, -sink, dz], [dx, h - sink, dz], [-dx, h - sink, dz],
      opts.treeCell, tint, tint, tint, tint, rng() < 0.5,
    )
  }
}

/** The bole. Vertex-coloured bark, UVs parked on the white cell. */
function trunk(s: Soup, H: number): void {
  const SIDES = 5
  const rings = [-H * 0.12, 0, H * 0.25, H * 0.55, H * 0.92]
  const radius = (y: number): number => {
    const t = Math.max(0, Math.min(1, y / H))
    const taper = 0.020 * (1 - t * 0.8)
    const flare = y < 0 ? 1 + (-y / H) * 3.4 : 1
    return H * taper * flare
  }
  for (let r = 0; r < rings.length - 1; r++) {
    const y0 = rings[r]!
    const y1 = rings[r + 1]!
    const r0 = radius(y0)
    const r1 = radius(y1)
    // Darker with height into the crown, where the cards shade it all day.
    const mid = (y0 + y1) * 0.5
    const shade = 1 - 0.6 * Math.min(1, Math.max(0, (mid / H - 0.05) / 0.3))
    const c: Col = [BARK[0] * shade, BARK[1] * shade, BARK[2] * shade]
    for (let i = 0; i < SIDES; i++) {
      const a0 = (i / SIDES) * Math.PI * 2
      const a1 = ((i + 1) / SIDES) * Math.PI * 2
      const p00: Vec = [Math.cos(a0) * r0, y0, Math.sin(a0) * r0]
      const p10: Vec = [Math.cos(a1) * r0, y0, Math.sin(a1) * r0]
      const p01: Vec = [Math.cos(a0) * r1, y1, Math.sin(a0) * r1]
      const p11: Vec = [Math.cos(a1) * r1, y1, Math.sin(a1) * r1]
      s.flat(p00, p01, p11, c)
      s.flat(p00, p11, p10, c)
    }
  }
}

/**
 * One photographed branch spray per branch site.
 *
 * The quad's plane contains the branch axis and is tilted off vertical by a
 * per-branch angle, so a crown of thirty cards covers every viewing direction
 * without any card facing the camera exactly — the classic foliage-card
 * arrangement. Tints darken toward the trunk: the crown shades its own
 * interior, and that inward falloff is what keeps a card tree from looking
 * like a paper lantern with the light on.
 */
function cards(s: Soup, opts: CardTreeOptions, rng: () => number): void {
  const H = opts.height
  const crownBase = H * 0.14
  const spireBase = H * 0.9

  for (let w = 0; w < opts.whorls; w++) {
    const t = opts.whorls === 1 ? 0 : w / (opts.whorls - 1)
    const y = crownBase + (spireBase - crownBase)
      * (Math.pow(t, 0.78) + (rng() - 0.5) * 0.05)
    const envelope = H * opts.spread * Math.max(0.3, Math.pow(1 - t, 0.75))
    const phase = rng() * Math.PI * 2

    for (let b = 0; b < opts.branchesPerWhorl; b++) {
      const jitter = rng()
      const roll = rng()
      const lenRoll = rng()
      const tiltRoll = rng()
      const flipRoll = rng()
      const briRoll = rng()

      const theta = phase + (b / opts.branchesPerWhorl) * Math.PI * 2
        + (jitter - 0.5) * 0.6
      const windward = (1 + Math.cos(theta)) * 0.5
      if (roll < opts.gaps + opts.windFlag * windward) continue

      const len = envelope * (0.7 + lenRoll * 0.5)
      if (len < H * 0.03) continue

      const droop = opts.droop * (1 - t * 0.5) * (0.7 + tiltRoll * 0.6)
      const ux = Math.cos(theta)
      const uz = Math.sin(theta)
      const dir: Vec = [ux * Math.cos(droop), -Math.sin(droop), uz * Math.cos(droop)]
      // Card up: world up tilted around the branch axis, so the deck of cards
      // covers many orientations.
      const tilt = (tiltRoll - 0.5) * 1.1
      const sx = -uz
      const sz = ux
      const upv: Vec = normalize([
        sx * Math.sin(tilt),
        Math.cos(tilt),
        sz * Math.sin(tilt),
      ])

      // Taller than the photo aspect strictly wants: the cards must overlap
      // vertically or the crown reads as separate shelves around a bare pole,
      // which is exactly how the first in-game shot came out.
      const hCard = (len / opts.branchCell.aspect) * 1.5
      const r0 = H * 0.012
      const base: Vec = [ux * r0, y, uz * r0]
      const tip: Vec = [
        base[0] + dir[0] * len,
        base[1] + dir[1] * len,
        base[2] + dir[2] * len,
      ]
      // The photo's branch axis sits a little below the cell's middle — snow
      // loaf above, needle fringe below.
      const upA = 0.42 * hCard
      const dnA = 0.58 * hCard

      const p00: Vec = [base[0] - upv[0] * dnA, base[1] - upv[1] * dnA, base[2] - upv[2] * dnA]
      const p10: Vec = [tip[0] - upv[0] * dnA, tip[1] - upv[1] * dnA, tip[2] - upv[2] * dnA]
      const p11: Vec = [tip[0] + upv[0] * upA, tip[1] + upv[1] * upA, tip[2] + upv[2] * upA]
      const p01: Vec = [base[0] + upv[0] * upA, base[1] + upv[1] * upA, base[2] + upv[2] * upA]

      const outer = 0.88 + briRoll * 0.12
      const inner = outer * 0.72
      const ci: Col = [inner, inner, inner]
      const co: Col = [outer, outer, outer]
      s.quad(p00, p10, p11, p01, opts.branchCell, ci, co, co, ci, flipRoll < 0.5)
    }
  }
}

/** The bare leader above the top whorl. */
function spire(s: Soup, H: number, rng: () => number): void {
  const top = H * (0.99 + rng() * 0.04)
  const leanX = (rng() - 0.5) * H * 0.04
  const leanZ = (rng() - 0.5) * H * 0.04
  const base = H * 0.9
  const r = H * 0.007
  const apex: Vec = [leanX, top, leanZ]
  const c: Col = [BARK[0] * 0.7, BARK[1] * 0.7, BARK[2] * 0.7]
  for (let i = 0; i < 4; i++) {
    const a0 = (i / 4) * Math.PI * 2
    const a1 = ((i + 1) / 4) * Math.PI * 2
    s.flat(
      [Math.cos(a0) * r, base, Math.sin(a0) * r],
      apex,
      [Math.cos(a1) * r, base, Math.sin(a1) * r],
      c,
    )
  }
}

function normalize(v: Vec): Vec {
  const l = Math.hypot(v[0], v[1], v[2]) || 1
  return [v[0] / l, v[1] / l, v[2] / l]
}
